import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button, Input, Label } from "../components/ui";

export function JoinGuestPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { user, token: authToken } = useAuth();
  const [preview, setPreview] = useState<{
    meeting: { id: string; title: string; host: { name: string } };
    requiresLogin: boolean;
  } | null>(null);
  const [guestName, setGuestName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .previewInvite(token)
      .then(setPreview)
      .catch((e) => setError(e instanceof Error ? e.message : "Invalid link"));
  }, [token]);

  useEffect(() => {
    if (preview?.requiresLogin && user && authToken) {
      sessionStorage.setItem(`invite_${preview.meeting.id}`, token);
      navigate(`/room/${preview.meeting.id}`);
    }
  }, [preview, user, authToken, token, navigate]);

  function joinAsGuest(e: FormEvent) {
    e.preventDefault();
    if (!preview || !guestName.trim()) return;
    sessionStorage.setItem("guest_invite_token", token);
    sessionStorage.setItem("guest_name", guestName.trim());
    navigate(`/room/${preview.meeting.id}`);
  }

  if (error) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <div className="glass-panel max-w-md rounded-2xl p-8 text-center">
          <p className="text-red-300">{error}</p>
          <Link to="/" className="mt-4 inline-block text-accent-400">
            Back home
          </Link>
        </div>
      </div>
    );
  }

  if (!preview) {
    return <div className="flex min-h-full items-center justify-center text-ink-500">Loading invite…</div>;
  }

  if (preview.requiresLogin) {
    return (
      <div className="flex min-h-full items-center justify-center p-6">
        <motion.div className="glass-panel max-w-md rounded-2xl p-8" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-xl font-semibold">{preview.meeting.title}</h1>
          <p className="mt-2 text-sm text-ink-500">
            Hosted by {preview.meeting.host.name}. This invite is for a registered account.
          </p>
          <Link to="/login" className="mt-6 block">
            <Button type="button" className="w-full">
              Sign in to join
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <motion.div className="glass-panel w-full max-w-md rounded-2xl p-8" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-display text-xl font-semibold">{preview.meeting.title}</h1>
        <p className="mt-1 text-sm text-ink-500">Guest invite · Host: {preview.meeting.host.name}</p>

        <form className="mt-6 space-y-4" onSubmit={joinAsGuest}>
          <div>
            <Label>Your name</Label>
            <Input required value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="How should we show you?" />
          </div>
          <Button type="submit" className="w-full">
            Join meeting
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
